/**
 * Pure calculations for the Cost Allocation tables.
 *
 * An allocation is a percentage of a line's budget, stored per line and column.
 * A cell's value is budget × % / 100. Department and cost-centre rows sum their
 * lines; their % is the allocated value over their budget. A line's total % is
 * the sum of its columns' percentages, so a zero-budget line still reports what
 * was entered against it.
 */

// Tolerance for float noise when deciding a row is over 100%.
export const OVER_TOLERANCE = 1e-6;

/**
 * A line is over when its columns add up past 100% and its budget is not negative. A
 * zero-budget line can be over; a negative project balance never is. The export uses
 * the same rule.
 */
export const isLineOver = (budget, pctSum) => budget >= 0 && pctSum > 100 + OVER_TOLERANCE;

/**
 * What is left to allocate. A negative project balance has nothing to allocate, so it
 * shows none. Every other row, parents and the total included, is budget − allocated.
 */
const unallocatedOf = (budget, allocated, isLine) => (isLine && budget < 0 ? 0 : budget - allocated);

export const departmentKey = (departmentId) => `d:${departmentId}`;
export const costcenterKey = (departmentId, costcenterId) => `c:${departmentId}_${costcenterId}`;
export const lineKey = (lineId) => `e:${lineId}`;
export const projectKey = (projectId) => `p:${projectId}`;

const emptyColumn = () => ({ value: 0, committed: 0, actuals: 0, any: false, blockedValue: 0 });

/** A project balance below zero cannot be allocated; anything already stored on it stays read-only. */
export const isBlockedLine = (line) => line.type === 'project' && (Number(line.budget) || 0) < 0;

/** The stored % of one line in one column, or null when not allocated. */
export const linePct = (allocations, lineId, columnId) => {
    const pct = allocations?.[lineId]?.[columnId];

    return pct === undefined || pct === null ? null : Number(pct);
};

const lineFigures = (line, columns, allocations) => {
    const blocked = isBlockedLine(line);
    const figures = {
        blocked,
        budget: Number(line.budget) || 0,
        committed: Number(line.committed) || 0,
        actuals: Number(line.actuals) || 0,
        columns: {},
        allocated: 0,
        pctSum: 0,
        any: false,
        lineCount: 1,
        writableCount: blocked ? 0 : 1,
        writableBudget: blocked ? 0 : Number(line.budget) || 0,
        overCount: 0,
        unallocatedCount: 0,
    };

    columns.forEach((column) => {
        const pct = linePct(allocations, line.id, column.id);
        const cell = emptyColumn();

        if (pct !== null) {
            cell.any = true;
            cell.pct = pct;
            cell.value = (figures.budget * pct) / 100;
            cell.committed = (figures.committed * pct) / 100;
            cell.actuals = (figures.actuals * pct) / 100;
            cell.blockedValue = blocked ? cell.value : 0;
            figures.allocated += cell.value;
            figures.pctSum += pct;
            figures.any = true;
        }

        figures.columns[column.id] = cell;
    });

    figures.overCount = isLineOver(figures.budget, figures.pctSum) ? 1 : 0;
    // A line with no budget, or a project balance of zero or less, has nothing to allocate.
    figures.unallocatedCount = figures.budget > 0 && !figures.any ? 1 : 0;

    return finish(figures, true);
};

const sumFigures = (children, columns) => {
    const figures = {
        budget: 0,
        committed: 0,
        actuals: 0,
        columns: {},
        allocated: 0,
        any: false,
        lineCount: 0,
        writableCount: 0,
        writableBudget: 0,
        overCount: 0,
        unallocatedCount: 0,
    };

    columns.forEach((column) => {
        figures.columns[column.id] = emptyColumn();
    });

    children.forEach((child) => {
        figures.budget += child.budget;
        figures.committed += child.committed;
        figures.actuals += child.actuals;
        figures.allocated += child.allocated;
        figures.any = figures.any || child.any;
        figures.lineCount += child.lineCount;
        figures.writableCount += child.writableCount;
        figures.writableBudget += child.writableBudget;
        figures.overCount += child.overCount;
        figures.unallocatedCount += child.unallocatedCount;

        columns.forEach((column) => {
            const cell = figures.columns[column.id];
            const childCell = child.columns[column.id];
            cell.value += childCell.value;
            cell.committed += childCell.committed;
            cell.actuals += childCell.actuals;
            cell.blockedValue += childCell.blockedValue;
            cell.any = cell.any || childCell.any;
        });
    });

    columns.forEach((column) => {
        const cell = figures.columns[column.id];
        cell.pct = figures.budget > 0 ? (cell.value / figures.budget) * 100 : null;
    });

    return finish(figures, false);
};

const finish = (figures, isLine) => {
    const totalPct = isLine ? figures.pctSum : figures.budget > 0 ? (figures.allocated / figures.budget) * 100 : null;

    return {
        ...figures,
        totalPct,
        unallocated: unallocatedOf(figures.budget, figures.allocated, isLine),
        // A parent is over only through a line beneath it, never because a negative
        // project balance pushes its own share past 100%.
        over: figures.overCount > 0,
    };
};

/**
 * Figures for every row, keyed by row key ("d:", "c:", "p:", "e:"), plus "total". A
 * project ("p:") sums its balance — its own expense line ("e:") — and its inner lines.
 * Unallocated is budget − allocated on every row except a negative project balance,
 * which has none.
 * Each entry: budget, committed, actuals, allocated, unallocated, totalPct, over, any,
 * lineCount, overCount, unallocatedCount, writableCount and writableBudget (the lines a
 * write reaches: all but negative project balances), and per column { value, committed,
 * actuals, pct, any, blockedValue (what negative balances hold, which a write keeps) }.
 */
export const computeFigures = (departments, columns, allocations) => {
    const figures = {};
    const departmentFigures = [];

    departments.forEach((department) => {
        const costcenterFigures = department.costcenters.map((costcenter) => {
            const lines = costcenter.lines.map((line) => {
                const lineResult = lineFigures(line, columns, allocations);
                figures[lineKey(line.id)] = lineResult;

                if (!line.children?.length) {
                    return lineResult;
                }

                const childResults = line.children.map((child) => {
                    const childResult = lineFigures(child, columns, allocations);
                    figures[lineKey(child.id)] = childResult;

                    return childResult;
                });
                const projectResult = sumFigures([lineResult, ...childResults], columns);
                figures[projectKey(line.id)] = projectResult;

                return projectResult;
            });
            const costcenterResult = sumFigures(lines, columns);
            figures[costcenterKey(department.id, costcenter.id)] = costcenterResult;

            return costcenterResult;
        });
        const departmentResult = sumFigures(costcenterFigures, columns);
        figures[departmentKey(department.id)] = departmentResult;
        departmentFigures.push(departmentResult);
    });

    figures.total = sumFigures(departmentFigures, columns);

    return figures;
};

/** Merge an allocation write's `{ expenseId: pct|null }` into the report's allocations for one column. */
export const mergeAllocations = (allocations, columnId, changes) => {
    const next = { ...allocations };

    Object.entries(changes || {}).forEach(([expenseId, pct]) => {
        const row = { ...(next[expenseId] || {}) };

        if (pct === null || pct === undefined) {
            delete row[columnId];
        } else {
            row[columnId] = Number(pct);
        }

        if (Object.keys(row).length === 0) {
            delete next[expenseId];
        } else {
            next[expenseId] = row;
        }
    });

    return next;
};

/** Drop one column from every line's allocations. */
export const withoutColumn = (allocations, columnId) => {
    const next = {};

    Object.entries(allocations || {}).forEach(([expenseId, row]) => {
        const { [columnId]: removed, ...rest } = row; // eslint-disable-line no-unused-vars

        if (Object.keys(rest).length > 0) {
            next[expenseId] = rest;
        }
    });

    return next;
};

/** Every department, cost-centre and project key, for Expand all. */
export const allParentKeys = (departments) =>
    departments.flatMap((department) => [
        departmentKey(department.id),
        ...department.costcenters.flatMap((cc) => [
            costcenterKey(department.id, cc.id),
            ...cc.lines.filter((line) => line.children?.length).map((line) => projectKey(line.id)),
        ]),
    ]);

/**
 * The rows on screen, in order: departments, their cost centres when expanded, those
 * cost centres' lines when expanded, and under an expanded project its balance and
 * its lines. Each row carries its key, the key its note is stored under (none for a
 * project's balance, whose note is the project's), depth, kind, name, the path shown
 * in editors and panels, and what it covers.
 */
export const visibleRows = (departments, expanded) => {
    const rows = [];

    departments.forEach((department) => {
        const dKey = departmentKey(department.id);
        rows.push({
            key: dKey,
            noteKey: dKey,
            depth: 0,
            kind: 'department',
            name: department.name,
            path: department.name,
            department,
            hasChildren: department.costcenters.length > 0,
        });

        if (!expanded.has(dKey)) {
            return;
        }

        department.costcenters.forEach((costcenter) => {
            const cKey = costcenterKey(department.id, costcenter.id);
            const ccPath = `${department.name} › ${costcenter.name}`;
            rows.push({
                key: cKey,
                noteKey: cKey,
                depth: 1,
                kind: 'costcenter',
                name: costcenter.name,
                path: ccPath,
                department,
                costcenter,
                hasChildren: costcenter.lines.length > 0,
            });

            if (!expanded.has(cKey)) {
                return;
            }

            costcenter.lines.forEach((line) => {
                const linePath = `${ccPath} › ${line.name}`;

                if (!line.children?.length) {
                    rows.push({
                        key: lineKey(line.id),
                        noteKey: lineKey(line.id),
                        depth: 2,
                        kind: 'line',
                        name: line.name,
                        path: linePath,
                        department,
                        costcenter,
                        line,
                        hasChildren: false,
                    });

                    return;
                }

                const pKey = projectKey(line.id);
                rows.push({
                    key: pKey,
                    noteKey: lineKey(line.id),
                    depth: 2,
                    kind: 'project',
                    name: line.name,
                    path: linePath,
                    department,
                    costcenter,
                    line,
                    hasChildren: true,
                });

                if (!expanded.has(pKey)) {
                    return;
                }

                const balanceName = $t('Project balance');
                rows.push({
                    key: lineKey(line.id),
                    noteKey: null,
                    depth: 3,
                    kind: 'line',
                    name: balanceName,
                    path: `${linePath} › ${balanceName}`,
                    department,
                    costcenter,
                    line,
                    hasChildren: false,
                });

                line.children.forEach((child) => {
                    rows.push({
                        key: lineKey(child.id),
                        noteKey: lineKey(child.id),
                        depth: 3,
                        kind: 'line',
                        name: child.name,
                        path: `${linePath} › ${child.name}`,
                        department,
                        costcenter,
                        line: child,
                        hasChildren: false,
                    });
                });
            });
        });
    });

    return rows;
};

/** The lines a department, cost-centre or project row covers: projects' balances and inner lines included. */
const linesBeneath = (row) => {
    const expand = (lines) => lines.flatMap((line) => [line, ...(line.children || [])]);

    if (row.kind === 'project') {
        return expand([row.line]);
    }

    if (row.kind === 'costcenter') {
        return expand(row.costcenter.lines);
    }

    return expand(row.department.costcenters.flatMap((costcenter) => costcenter.lines));
};

/**
 * How many lines beneath a department, cost-centre or project row would be over 100%
 * once `pct` (null clears) is written to them in one column: each line's other columns
 * plus `pct`. Negative project balances are not written, so never counted.
 */
export const overCountAfter = (row, columns, allocations, columnId, pct) =>
    linesBeneath(row).filter((line) => {
        if (isBlockedLine(line)) {
            return false;
        }

        const others = columns.reduce((sum, column) => (column.id === columnId ? sum : sum + (linePct(allocations, line.id, column.id) ?? 0)), 0);

        return isLineOver(Number(line.budget) || 0, others + (pct ?? 0));
    }).length;
